import { ApplicationLayoutBoxShadow } from "@am-tax/fe-core";
import {
    AMLogoBlack,
    AMLogoColor,
    AMLogoWhite,
    AMTaxLogoUsColor,
    AMTaxLogoUsBlack,
    AMTaxLogoUsWhite,
    AMTaxLogoGlobalColor,
    AMTaxLogoGlobalBlack,
    AMTaxLogoGlobalWhite,
} from "./Logos";
import { Box, Divider, Flex, FlexProps, Heading, IconButton, Image, Text, TextProps } from "@chakra-ui/react";
import { cloneElement, FC, Fragment, isValidElement, ReactElement, ReactNode, useEffect, useState } from "react";
import { IconChevronsLeft, IconChevronsRight } from "@tabler/icons-react";
import { useMatch } from "react-router-dom";
import { NavItem, NavItemProps } from "./NavItem";
import { Constants } from "../../utils";

export type NavGroup = {
    isGroup: true;
    groupColor?: string;
    groupHeading?: ReactElement | string;
    items: Array<NavItemProps | ReactElement>;
};

export type LogoObject = { type: "us" | "global" | ""; color?: "white" | "black" | "color" };

export type LeftNavProps = {
    logo: ReactElement | LogoObject;
    appTitle?: string;
    appTitleProps?: TextProps;
    items: Array<NavItemProps | NavGroup | ReactElement>;
    itemsHeader?: ReactNode;
    itemsFooter?: Array<NavItemProps | NavGroup | ReactElement>;
    startCollapsed?: boolean;
    collapsable?: boolean;
} & FlexProps;

export const LeftNavCore: FC<LeftNavProps> = ({
    logo,
    appTitle,
    appTitleProps,
    items,
    itemsHeader,
    itemsFooter,
    startCollapsed,
    collapsable,
    ...flexProps
}) => {
    const isNotClientPage = !useMatch("/clients");
    const [collapsed, setCollapsed] = useState(startCollapsed || isNotClientPage);
    const adminPageMatch = useMatch("/admin/*");
    const colorScheme = adminPageMatch ? "gray" : "blue";
    let logoAs = null;
    let isGlobal = false;

    if (logo && Object.hasOwn(logo, "type")) {
        const logoObj = logo as LogoObject;

        if (collapsed) {
            if (logoObj.color === "color") {
                logoAs = AMLogoColor;
            } else if (logoObj.color === "black") {
                logoAs = AMLogoBlack;
            } else {
                logoAs = AMLogoWhite;
            }
        } else if (logoObj.type === "us") {
            if (logoObj.color === "color") {
                logoAs = AMTaxLogoUsColor;
            } else if (logoObj.color === "black") {
                logoAs = AMTaxLogoUsBlack;
            } else {
                logoAs = AMTaxLogoUsWhite;
            }
        } else {
            isGlobal = true;

            if (logoObj.color === "color") {
                logoAs = AMTaxLogoGlobalColor;
            } else if (logoObj.color === "black") {
                logoAs = AMTaxLogoGlobalBlack;
            } else {
                logoAs = AMTaxLogoGlobalWhite;
            }
        }
    }

    const toggleCollapse = () => {
        setCollapsed(!collapsed && isNotClientPage);
    };

    const getFragment = (index: number, item: any) => {
        const _item = cloneElement(item as ReactElement, {
            ...item.props,
            collapsed: collapsed,
        });

        return <Fragment key={index}>{isValidElement(item) && "props" in _item && "text" in _item.props ? _item : item}</Fragment>;
    };

    useEffect(() => {
        if (!isNotClientPage) {
            setTimeout(() => {
                setCollapsed(false);
            }, 0);
        }
    }, [collapsed, isNotClientPage]);

    return (
        <Flex
            height="100%"
            width={collapsed ? "3rem" : "165pt"}
            maxWidth="180pt"
            gap="1rem"
            direction="column"
            grow={1}
            backgroundColor="blue.900"
            bgGradient="linear(to-b, blue.900, blue.700)"
            boxShadow={ApplicationLayoutBoxShadow}
            {...flexProps}
        >
            <Flex direction="column" p="1rem" align="center" gap=".5rem">
                {logoAs ? (
                    <Image
                        as={logoAs}
                        width={collapsed ? "24pt" : isGlobal ? "130pt" : "150pt"}
                        height={collapsed ? "24pt" : isGlobal ? "39.43pt" : "45.5pt"}
                        aria-label="Alvarez and Marsal Tax Logo"
                    />
                ) : (
                    (logo as ReactElement)
                )}
                {!!appTitle && (
                    <Text color="white" fontFamily="DIN Condensed" textTransform="uppercase" letterSpacing="2.25pt" fontWeight="300" {...appTitleProps}>
                        {collapsed ? Constants.emptyString : appTitle}
                    </Text>
                )}
            </Flex>
            <Divider borderColor="whiteAlpha.200" mx={0} />
            {collapsed ? <div style={{ height: "25pt" }} /> : <></>}

            {itemsHeader}

            <Flex direction="column" height="100%">
                {items.map((item, index) => {
                    if (Object.hasOwn(item, "isGroup")) {
                        const group = item as NavGroup;
                        const wrapHeading = typeof group.groupHeading === "string";
                        return (
                            <Flex key={index} direction="column" m={"1rem 0"} borderLeft={"3pt solid"} borderColor={group.groupColor || "yellow.400"}>
                                {!collapsed && wrapHeading ? (
                                    <Heading
                                        variant={"info"}
                                        pt={"1rem"}
                                        py={".5rem"}
                                        size={"sm"}
                                        whiteSpace={"nowrap"}
                                        overflow={"hidden"}
                                        textOverflow={"ellipsis"}
                                        maxWidth={"100%"}
                                        color={"whiteAlpha.700"}
                                    >
                                        {group.groupHeading}
                                    </Heading>
                                ) : !collapsed && group.groupHeading ? (
                                    group.groupHeading
                                ) : null}
                                {group.items.map((item, index) => {
                                    if (Object.hasOwn(item, "to")) {
                                        return <NavItem key={index} collapsed={collapsed} {...(item as NavItemProps)} />;
                                    } else {
                                        return getFragment(index, item);
                                    }
                                })}
                            </Flex>
                        );
                    } else if (Object.hasOwn(item, "to")) {
                        return <NavItem key={index} collapsed={collapsed} {...(item as NavItemProps)} />;
                    } else {
                        return getFragment(index, item);
                    }
                })}
            </Flex>

            <Flex direction="column" m={"0"} p={"0"}>
                {(itemsFooter || []).map((item, index) => {
                    return Object.hasOwn(item, "to") ? (
                        <NavItem key={`itemsFooter_${index}`} collapsed={collapsed} {...(item as NavItemProps)} />
                    ) : (
                        getFragment(index, item)
                    );
                })}
            </Flex>

            {collapsable ? (
                <Flex direction="column" width="100%" height="37pt" justifyContent="space-between">
                    <Box
                        mb="20pt"
                        mr={collapsed ? Constants.emptyString : "5pt"}
                        width={collapsed ? "100%" : undefined}
                        display={collapsed ? "flex" : undefined}
                        justifyContent={collapsed ? "center" : undefined}
                        alignSelf={collapsed ? undefined : "flex-end"}
                    >
                        <IconButton
                            aria-label={collapsed ? "Expand" : "Collapse"}
                            icon={collapsed ? <IconChevronsRight size="1.5rem" stroke={1} /> : <IconChevronsLeft size="1.5rem" stroke={1} />}
                            size="lg"
                            colorScheme={colorScheme}
                            onClick={toggleCollapse}
                            variant="ghost"
                            color="white"
                            _hover={{ bg: "whiteAlpha.200" }}
                        />
                    </Box>
                </Flex>
            ) : (
                <></>
            )}
        </Flex>
    );
};
